import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "crop-knowledge-engines",
  kind: "domain-vertical",
  title: "Crop Knowledge & Agronomy Systems",
  shortTitle: "Crop Knowledge",
  status: "draft",
  tier: "we-do",
  seoTitle: "Crop Knowledge & Agronomy Systems | General Applications",
  seoDescription:
    "Structured botanical taxonomies, cultivar trait databases, growing parameter models, and agronomic search engines for agricultural software.",
  summary:
    "Structured botanical taxonomies, cultivar catalogs, growing condition models, and multi-source agronomic search engines that separate botanical reality from commercial marketing.",
  image: "/images/blog/search-relevance-taxonomy-hero.webp",
  relatedCapabilitySlugs: [],
  relatedIndustrySlugs: ["agriculture"],
  relatedPostSlugs: [
    "search-relevance-taxonomy",
    "deterministic-controls-ai-agents",
    "closed-loop-greenhouses",
  ],
  engineering: [
    "Three-axis agronomic data models separating botanical identity (ICN), cultivated varieties (ICNCP), and vernacular crop names",
    "Comprehensive cultivar trait modeling: days to maturity, spacing, frost tolerance, growth habit, and resistance profiles",
    "Automated ingestion and normalization pipelines for university extensions, USDA databases, and commercial seed catalogs",
    "Faceted taxonomic search with vernacular synonym resolution, botanical rank hierarchy traversal, and parentage tracking",
    "Bounded AI-assisted cultivar drafting with deterministic fact-group validation (maturity, spacing, sizing) and human editorial review",
    "Environmental condition modeling mapping USDA hardiness zones, growing degree days (GDD), photoperiods, and soil requirements",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "Separating Botanical Truth from Seed Catalog Prose",
    },
    {
      type: "paragraph",
      text: "Agricultural information is frequently compromised by conflating botanical identity, commercial trade names, and culinary definitions. The same botanical taxon may be sold under dozens of conflicting trademarked names, while seed catalogs routinely blend objective phenotypic data with subjective marketing claims. Useful agronomic systems separate these concerns: what a plant is botanically (ICN taxonomy), what has been selected and bred (ICNCP cultivars and groups), and what growers call and cultivate (vernacular crops).",
    },
    {
      type: "heading",
      level: 2,
      text: "Structured Agronomy Powers Reliable Planning",
    },
    {
      type: "paragraph",
      text: "When crop and cultivar data is modeled with structural integrity, downstream agricultural applications gain predictable foundations. Systems can calculate exact planting windows based on frost tolerances, verify spatial row spacings against mature canopy habits, assess family-level disease susceptibilities for rotation planning, and ground AI-assisted agronomic tools in validated, citation-backed facts rather than unconstrained generative guesses.",
    },
  ],
  phases: {
    study: {
      focus:
        "Audit agronomic references, seed catalogs, taxonomic sources, and grower databases to establish canonical data requirements.",
      produces: [
        "Botanical, cultivar, and crop source data audit and taxonomic gap analysis",
        "Phenotypic attribute schema and fact-group categorization requirements",
        "Source provenance and confidence-scoring model specifications",
        "Search query patterns and agronomic retrieval use-case evaluations",
      ],
      fit: {
        good: [
          "AgTech platforms, seed producers, botanical institutions, and commercial farm networks requiring verified, structured crop reference data",
          "Organizations building grower-facing applications that need precise taxonomic search and reliable planting parameters",
        ],
        poor: [
          "Operations looking for uncurated text-scraping tools without schema enforcement, taxonomic rigor, or human editorial review",
          "Casual gardening projects where loose keyword tags and unverified informal advice are sufficient",
        ],
      },
    },
    "initial-design": {
      focus:
        "Design the three-axis taxonomic architecture, cultivar schema, normalization pipelines, and search index structures.",
      produces: [
        "Three-axis relational schema design (botanical ranks, cultivars, vernacular crops)",
        "Cultivar fact-group schemas (botanical classification, maturity, spacing, cultural needs)",
        "Multi-source ETL normalization and duplicate-resolution architecture",
        "Faceted agronomic search and synonym mapping specifications",
      ],
    },
    "detailed-design": {
      focus:
        "Specify ingestion connectors, deterministic validation rules, bounded AI drafting pipelines, and API contracts.",
      produces: [
        "Seed-catalog and university extension crawler and normalization rules",
        "Bounded AI cultivar drafting pipeline specifications with role separation (reader, quoter, writer, editor)",
        "Faceted search index configurations and rank-traversal query definitions",
        "REST and GraphQL API schemas for downstream application integration",
      ],
    },
    implementation: {
      focus:
        "Build and deploy the crop knowledge engine, execute reference data ingestion, and deliver public or internal catalog interfaces.",
      produces: [
        "Production crop knowledge database and agronomic search engine",
        "Validated catalog of species, crops, and cultivars with full source citations",
        "Editorial review workflows and automated ingest validation test suites",
        "Developer documentation and typed API client libraries",
      ],
    },
  },
};
